// src/hooks/useAnnCostInvestmentSummary.js
import { useMemo } from "react";
import { useAnnCostInvBySector } from "./useAnnCostInvBySector";

export function useAnnCostInvestmentSummary({
  scenario,
  sectors = ["ELC", "IND", "TRA", "RES"], // only these four
  baselineYear = 2025,
  targetYear = 2050,
  from = 2010,
  to = 2050,
  households = 28_000_000, // change if you have a better figure
  pvToGBP = 1,             // set to 1e6 if PV is in £ millions
} = {}) {
  const { labels, bySectorYear, loading, error } =
    useAnnCostInvBySector({ scenario, sectors, from, to });
    pvToGBP=0.001;
  // normalise to numeric years
  const years = useMemo(
    () =>
      (labels ?? [])
        .map((y) => (typeof y === "number" ? y : parseInt(y, 10)))
        .filter(Number.isFinite)
        .sort((a, b) => a - b),
    [labels]
  );

  const investmentDetails = useMemo(() => {
    if (loading || error || !years.length || !households) return null;

    const baseIdx = years.includes(baselineYear) ? years.indexOf(baselineYear) : 0;
    const tgtIdx  = years.includes(targetYear)   ? years.indexOf(targetYear)   : years.length - 1;

    const sumAt = (idx) =>
      sectors.reduce((sum, code) => {
        const v = bySectorYear?.[code]?.[years[idx]] ?? 0;
        return sum + (typeof v === "number" ? v : 0);
      }, 0);

    const baseTotalPV = sumAt(baseIdx);
    const tgtTotalPV  = sumAt(tgtIdx);

    const tgtGBPperHH = (tgtTotalPV * pvToGBP) / households;
    const pctChange   = baseTotalPV === 0 ? 0 : ((tgtTotalPV - baseTotalPV) / baseTotalPV) * 100;

    return {
      scenario,
      baselineYear: years[baseIdx],
      targetYear: years[tgtIdx],
      totalInvestmentPV: Math.round(tgtTotalPV * pvToGBP).toLocaleString("en-GB"),
      annualInvestmentPerHousehold: Math.round(tgtGBPperHH).toLocaleString("en-GB"),
      investmentIncrease: +(Math.round(pctChange * 10) / 10).toFixed(1), // signed number like -5.4 or 12.3
    };
  }, [years, baselineYear, targetYear, sectors, bySectorYear, households, pvToGBP, scenario, loading, error]);

  return { investmentDetails, loading, error };
}

